import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { getAuditLogs } from "../api";
import { toast } from "../components/Toast";
import "./AuditLogPage.css";

function buildDescription(log) {
  const user = log.user_department || log.user_email || "უცნობი";
  const subject = log.email_subject ? `"${log.email_subject}"` : "";
  const d = log.details || {};

  switch (log.action) {
    case "view":
      return `${user} - ნახა წერილი ${subject}`;

    case "edit":
      if (d.action === "generate_reply") {
        const choice = d.choice === "Yes" ? "დადებითი" : "უარყოფითი";
        return `${user} - დააგენერირა ${choice} პასუხი წერილზე ${subject}`;
      }
      if (d.action === "send_for_approval") {
        return `${user} - გაგზავნა დასამტკიცებლად წერილის პასუხი ${subject}`;
      }
      if (d.action === "reassign") {
        return `${user} - გადაანაწილა წერილი ${subject} (${d.from} → ${d.to})`;
      }
      return `${user} - შეცვალა წერილი ${subject}`;

    case "send":
      if (d.action === "send_directly") {
        return `${user} - გაგზავნა პასუხი პირდაპირ წერილზე ${subject}`;
      }
      if (d.action === "approved_and_sent") {
        return `${user} - დაამტკიცა და გაგზავნა პასუხი წერილზე ${subject}`;
      }
      if (d.action === "forward") {
        return `${user} - გადაუგზავნა წერილი ${subject} → ${d.recipient || ""}`;
      }
      return `${user} - გაგზავნა წერილი ${subject}`;

    case "approve":
      if (d.decision === "approved") {
        return `${user} - დაამტკიცა პასუხი წერილზე ${subject}`;
      }
      if (d.decision === "rejected") {
        const comment = d.comment ? `: "${d.comment}"` : "";
        return `${user} - უარყო პასუხი წერილზე ${subject}${comment}`;
      }
      return `${user} - განიხილა წერილი ${subject}`;

    case "delete":
      return `${user} - წაშალა წერილი ${subject}`;

    default:
      return `${user} - ${log.action} ${subject}`;
  }
}

const ACTION_LABELS = {
  view: "ნახვა",
  edit: "ცვლილება",
  send: "გაგზავნა",
  delete: "წაშლა",
  approve: "დამტკიცება",
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const navigate = useNavigate();
  const departmentData = JSON.parse(localStorage.getItem("department")) || {};
  const isSuperuser = departmentData.is_superuser || false;

  async function loadLogs() {
    setLoading(true);
    try {
      const data = await getAuditLogs();
      setLogs(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      toast(err.message, "error");
    }
    setLoading(false);
  }

  useEffect(() => {
    loadLogs();
  }, []);

  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ka-GE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const filteredLogs = filterAction === "all"
    ? logs
    : logs.filter((l) => l.action === filterAction);

  return (
    <div className="dash-root">
      <Header />
      <div className="dash-layout">
        <Sidebar activePage="audit" isSuperuser={isSuperuser} />

        <div className="al-content">
          <div className="al-header">
            <h2 className="al-title">აუდიტ ლოგი</h2>
            <div className="al-filters">
              {["all", "view", "edit", "send", "approve", "delete"].map((key) => (
                <button
                  key={key}
                  className={`al-filter-btn ${filterAction === key ? "active" : ""} ${key !== "all" ? key : ""}`}
                  onClick={() => setFilterAction(key)}
                >
                  {key === "all" ? "ყველა" : ACTION_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="al-loading">
              <div className="al-spinner" />
              იტვირთება...
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="al-empty">
              <div className="al-empty-icon">&#128203;</div>
              <div>ჩანაწერები არ არის</div>
            </div>
          ) : (
            <div className="al-timeline">
              {filteredLogs.map((log) => (
                <div key={log.id} className="al-entry-wrapper">
                  <div
                    className="al-entry"
                    onClick={() => log.email && navigate(`/dashboard?email=${log.email}`)}
                    style={{ cursor: log.email ? "pointer" : "default" }}
                  >
                    <div className={`al-dot ${log.action}`} />
                    <div className="al-entry-content">
                      <div className="al-entry-main">
                        <span className={`al-badge ${log.action}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                        <span className="al-description">{buildDescription(log)}</span>
                        {log.reply_text && (
                          <button
                            className="al-expand-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(expandedId === log.id ? null : log.id);
                            }}
                          >
                            {expandedId === log.id ? "▲ დამალვა" : "▼ პასუხის ნახვა"}
                          </button>
                        )}
                      </div>
                      <div className="al-entry-time">
                        {formatDate(log.timestamp)}
                        {log.email && <span className="al-goto">&#8594;</span>}
                      </div>
                    </div>
                  </div>
                  {expandedId === log.id && log.reply_text && (
                    <div className="al-reply-preview">
                      <div className="al-reply-label">გაგზავნილი პასუხი:</div>
                      <div className="al-reply-text">{log.reply_text}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
